import React, { useRef, useCallback } from 'react';
import { IcoPlus } from './EditIcons';
import { Label, InputBox, SelectBox, FieldRow, Field, SkillTag, SkillCategory } from './EditShared';
import styles from './EditBasicInfoSection.module.css';

const SKILL_CATEGORIES = [
  {
    id: 'language', label: '언어 / 사투리',
    groups: [
      { name: '외국어', items: ['영어(회화)', '영어(비즈니스)', '영어(네이티브)', '일본어', '중국어(표준)', '중국어(광둥)', '프랑스어', '스페인어', '독일어'] },
      { name: '사투리', items: ['서울/표준어', '경상도', '전라도', '충청도', '강원도', '제주도', '북한'] },
    ],
  },
  {
    id: 'action', label: '액션 / 무술',
    groups: [{ name: '', items: ['태권도', '유도', '검도', '복싱', '무에타이', '킥복싱', '합기도', '공수도', '쿵푸', '스턴트', '격투기'] }],
  },
  {
    id: 'vocal', label: '노래 / 보컬',
    groups: [{ name: '', items: ['팝', '발라드', '뮤지컬', '재즈', '힙합/R&B', '클래식(성악)', '록/메탈', '트로트'] }],
  },
  {
    id: 'instrument', label: '악기',
    groups: [{ name: '', items: ['피아노', '기타', '베이스기타', '드럼', '바이올린', '첼로', '플루트', '색소폰', '트럼펫', '해금', '가야금'] }],
  },
  {
    id: 'dance', label: '춤 / 퍼포먼스',
    groups: [{ name: '', items: ['K-팝', '힙합', '브레이크댄스', '발레', '재즈댄스', '탭댄스', '한국무용', '현대무용', '라틴댄스', '폴댄스'] }],
  },
  {
    id: 'sports', label: '스포츠 / 신체활동',
    groups: [{ name: '', items: ['수영', '승마', '스케이트보드', '스키/스노보드', '서핑', '다이빙', '체조', '펜싱', '골프', '테니스', '탁구', '농구', '축구', '야구'] }],
  },
];

export default function EditBasicInfoSection({
  name, setName, englishName, setEnglishName,
  stage, setStage,
  gender, setGender, birth, setBirth,
  height, setHeight, weight, setWeight, location, setLocation,
  bio, setBio, agency, setAgency,
  selectedSkills, setSelectedSkills, customSkill, setCustomSkill,
  skillsOnly = false,
  extrasOnly = false,
  hideSkills = false,
  hideHeader = false,
}) {
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);
  const isComposingRef = useRef(false);

  function toggleSkill(item) {
    setSelectedSkills(prev => prev.includes(item) ? prev.filter(s => s !== item) : [...prev, item]);
  }
  function removeSkill(item) {
    setSelectedSkills(prev => prev.filter(s => s !== item));
  }
  function addCustomSkill() {
    const v = customSkill.trim();
    if (v && !selectedSkills.includes(v)) {
      setSelectedSkills(prev => [...prev, v]);
      setCustomSkill('');
    }
  }

  function handleDragStart(e, idx) {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  }
  function handleDragEnter(idx) {
    dragOverIdx.current = idx;
  }
  function handleDragEnd() {
    const from = dragIdx.current;
    const to = dragOverIdx.current;
    if (from !== null && to !== null && from !== to) {
      setSelectedSkills(prev => {
        const next = [...prev];
        const [item] = next.splice(from, 1);
        next.splice(to, 0, item);
        return next;
      });
    }
    dragIdx.current = null;
    dragOverIdx.current = null;
  }

  return (
    <div className={styles.container}>
      {!hideHeader && (
        <div>
          <h2 className={styles.title}>{skillsOnly ? '특기' : extrasOnly ? '프로필 정보' : '기본 정보'}</h2>
          <p className={styles.description}>{skillsOnly ? '프로필 편집과 동일한 방식으로 특기를 선택하세요.' : extrasOnly ? '거주지, 소개, 소속과 특기를 입력하세요.' : '기본적인 프로필 정보를 입력하세요.'}</p>
        </div>
      )}

      <div className={styles.card}>
        {!skillsOnly && !extrasOnly && (
          <>
            <FieldRow>
              <Field label="이름" required>
                <InputBox value={name} onChange={e => setName(e.target.value)} />
              </Field>
              <Field label="영문 이름">
                <InputBox value={englishName ?? ''} onChange={e => setEnglishName?.(e.target.value)} placeholder="English Name" />
              </Field>
              <Field label="활동명" required>
                <InputBox value={stage} onChange={e => setStage(e.target.value)} />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="성별" required>
                <SelectBox
                  value={gender} onChange={e => setGender(e.target.value)}
                  options={[{ value: '여성', label: '여성' }, { value: '남성', label: '남성' }]}
                />
              </Field>
              <Field label="생년월일" required>
                <input
                  type="date" value={birth} onChange={e => setBirth(e.target.value)}
                  className={styles.dateInput}
                />
              </Field>
            </FieldRow>

            <FieldRow>
              <Field label="키(cm)" required>
                <InputBox value={height} onChange={e => setHeight(e.target.value)} />
              </Field>
              <Field label="몸무게(kg)" required>
                <InputBox value={weight} onChange={e => setWeight(e.target.value)} />
              </Field>
              <Field label="거주지" required>
                <InputBox value={location} onChange={e => setLocation(e.target.value)} />
              </Field>
            </FieldRow>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Label required>소개</Label>
              <textarea
                value={bio} onChange={e => setBio(e.target.value)}
                className={styles.bioTextarea}
              />
            </div>

            <div className={styles.fieldGroup}>
              <Label>소속</Label>
              <InputBox value={agency} onChange={e => setAgency(e.target.value)} />
            </div>
          </>
        )}

        {extrasOnly && (
          <>
            <FieldRow>
              <Field label="거주지" required>
                <InputBox value={location} onChange={e => setLocation(e.target.value)} />
              </Field>
              <Field label="소속">
                <InputBox value={agency} onChange={e => setAgency(e.target.value)} />
              </Field>
            </FieldRow>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Label required>소개</Label>
              <textarea
                value={bio} onChange={e => setBio(e.target.value)}
                className={styles.bioTextarea}
              />
            </div>
          </>
        )}

        {!hideSkills && (
        <div className={styles.fieldGroup}>
          <Label required>특기</Label>
          {selectedSkills.length > 0 && (
            <div className={styles.skillList}>
              {selectedSkills.map((s, idx) => (
                <div
                  key={s}
                  draggable
                  onDragStart={e => handleDragStart(e, idx)}
                  onDragEnter={() => handleDragEnter(idx)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  style={{ cursor: 'grab' }}
                >
                  <SkillTag label={s} onRemove={() => removeSkill(s)} />
                </div>
              ))}
            </div>
          )}
          <div className={styles.skillSelectArea}>
            {SKILL_CATEGORIES.map(cat => (
              <SkillCategory key={cat.id} category={cat} selected={selectedSkills} onToggle={toggleSkill} />
            ))}
            <div className={styles.customInputRow}>
              <input
                value={customSkill} onChange={e => setCustomSkill(e.target.value)}
                onCompositionStart={() => { isComposingRef.current = true; }}
                onCompositionEnd={() => { isComposingRef.current = false; }}
                onKeyDown={e => { if (e.key === 'Enter' && !isComposingRef.current) addCustomSkill(); }}
                placeholder="직접 입력"
                className={styles.customSkillInput}
              />
              <button
                onClick={addCustomSkill}
                className={styles.addCustomBtn}
              >
                <IcoPlus />
              </button>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
